import * as React from 'react';
import {
    Box,
    Button,
    Card,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Typography,
    IconButton,
    Tooltip
} from '@mui/material';

import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DeleteIcon from '@mui/icons-material/Delete';

import AdminLayout from '../components/AdminLayout';
import Api from '../services/Api';
import { CheckCircleOutline, LeaderboardOutlined } from '@mui/icons-material';

// -----------------------------
// DTOs / modelos según api.json
// -----------------------------

export interface IMiniGamePointsDto {
    miniGameId: number;
    miniGameName: string | null;
    points: number;
}

export interface IAvailablePrizeDto {
    prizeId: number;
    name: string | null;
    description: string | null;
    imageUrl: string | null;
    pointsCost: number;
    stock: number | null;
}

export interface IAssignedPrizeDto {
    playerPrizeId: number;
    prizeId: number;
    name: string | null;
    description: string | null;
    imageUrl: string | null;
    pointsCost: number;
    quantity: number;
    pointsSpent: number;
    createdAt: string;
}

export interface IPlayerClassificationDto {
    playerId: number;
    firstName: string | null;
    lastName: string | null;
    fullName: string | null;
    totalPoints: number;
    miniGamePoints: IMiniGamePointsDto[] | null;
    availablePrizes: IAvailablePrizeDto[] | null;
    assignedPrizes: IAssignedPrizeDto[] | null;
}

export interface IPrizeDto {
    id: number;
    tournamentId: number;
    miniGameId: number | null;
    name: string | null;
    description: string | null;
    imageUrl: string | null;
    pointsCost: number;
    stock: number | null;
    isActive: boolean;
}

// -----------------------------
// Props / State
// -----------------------------

export interface ILeaderboardPageProps { }

export interface ILeaderboardPageState {
    tournamentId: number | null;
    classifications: IPlayerClassificationDto[];
    prizes: IPrizeDto[];
    loading: boolean;
    error: string | null;
    searchText: string;
    selectedPrizeByPlayer: { [playerId: number]: number | null };
    assigningForPlayerId: number | null;
    deletingPlayerPrizeId: number | null;
}

// -----------------------------
// Página LeaderBoard para admins
// -----------------------------

export default class LeaderboardPage extends React.Component<
    ILeaderboardPageProps,
    ILeaderboardPageState
> {
    public constructor(props: ILeaderboardPageProps) {
        super(props);

        this.state = {
            tournamentId: null,
            classifications: [],
            prizes: [],
            loading: false,
            error: null,
            searchText: '',
            selectedPrizeByPlayer: {},
            assigningForPlayerId: null,
            deletingPlayerPrizeId: null
        };
    }

    public componentDidMount(): void {
        this.loadData();
    }

    // -----------------------------
    // Helpers
    // -----------------------------

    private getTournamentIdFromQuery(): number | null {
        const params = new URLSearchParams(window.location.search);
        const tid = params.get('tournamentId');
        if (!tid) return null;

        const value = parseInt(tid, 10);
        return Number.isNaN(value) ? null : value;
    }

    private getCurrentUserId(): number {
        try {
            const userJson = localStorage.getItem('vg_user');
            if (!userJson) return 0;
            const user = JSON.parse(userJson);
            return typeof user.id === 'number' ? user.id : 0;
        } catch {
            return 0;
        }
    }

    private formatDateTime(dateString: string): string {
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return dateString;
        return d.toLocaleString();
    }

    /**
     * Puntos disponibles = totalPoints - suma de pointsSpent en premios asignados.
     * Nunca devolvemos menos de 0.
     */
    private getAvailablePoints(
        classification: IPlayerClassificationDto
    ): number {
        const assigned = classification.assignedPrizes || [];
        const spent = assigned.reduce(
            (sum, p) => sum + (p.pointsSpent || 0),
            0
        );
        const available = classification.totalPoints - spent;
        return available < 0 ? 0 : available;
    }

    /**
     * Stock restante global para un premio:
     * stock base (tabla Prizes) - suma de quantity en todos los assignedPrizes de todos los jugadores.
     * Si stock es null => sin límite => devolvemos null para indicar "no controlar".
     */
    private getRemainingStock(prizeId: number): number | null {
        const { prizes, classifications } = this.state;

        const basePrize = prizes.find((p) => p.id === prizeId);
        if (!basePrize) return null; // si no lo encontramos, no controlamos
        if (basePrize.stock === null) return null; // sin límite

        const baseStock = basePrize.stock;

        let assignedQuantity = 0;
        classifications.forEach((c) => {
            (c.assignedPrizes || []).forEach((ap) => {
                if (ap.prizeId === prizeId) {
                    assignedQuantity += ap.quantity || 0;
                }
            });
        });

        const remaining = baseStock - assignedQuantity;
        return remaining < 0 ? 0 : remaining;
    }

    // -----------------------------
    // Carga de datos
    // -----------------------------

    private loadData = async (): Promise<void> => {
        const tournamentIdFromQuery = this.getTournamentIdFromQuery();

        if (!tournamentIdFromQuery) {
            this.setState({
                loading: false,
                error: 'No se ha especificado el torneo (tournamentId).'
            });
            return;
        }

        this.setState({
            loading: true,
            error: null,
            tournamentId: tournamentIdFromQuery
        });

        try {
            const [classResponse, prizesResponse] = await Promise.all([
                Api.get(`/api/Classifications/tournament/${tournamentIdFromQuery}`),
                Api.get(`/api/Prizes/by-tournament/${tournamentIdFromQuery}`)
            ]);

            if (!classResponse.ok) {
                throw new Error('No se pudo cargar la clasificación del torneo.');
            }

            if (!prizesResponse.ok) {
                throw new Error('No se pudieron cargar los premios del torneo.');
            }

            const classifications: IPlayerClassificationDto[] =
                await classResponse.json();
            const prizes: IPrizeDto[] = await prizesResponse.json();

            // Ordenar jugadores por puntos totales descendente
            classifications.sort((a, b) => b.totalPoints - a.totalPoints);

            this.setState({
                classifications,
                prizes,
                loading: false,
                error: null,
                searchText: '',
                selectedPrizeByPlayer: {},
                assigningForPlayerId: null,
                deletingPlayerPrizeId: null
            });
        } catch (error: any) {
            this.setState({
                loading: false,
                error:
                    error.message ||
                    'Error al cargar la clasificación y los premios.'
            });
        }
    };

    // -----------------------------
    // Eventos UI
    // -----------------------------

    private handleSearchChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void => {
        this.setState({ searchText: event.target.value });
    };

    private handlePrizeSelectChange =
        (playerId: number) =>
            (event: SelectChangeEvent): void => {
                const value = event.target.value;
                const prizeId = value ? Number(value) : null;

                this.setState((prev) => ({
                    selectedPrizeByPlayer: {
                        ...prev.selectedPrizeByPlayer,
                        [playerId]: prizeId
                    }
                }));
            };

    private handleAssignPrize = async (
        classification: IPlayerClassificationDto
    ): Promise<void> => {
        const { tournamentId, selectedPrizeByPlayer, prizes } = this.state;
        if (!tournamentId) {
            alert('No se ha encontrado el torneo.');
            return;
        }

        const selectedPrizeId =
            selectedPrizeByPlayer[classification.playerId] ?? null;

        if (selectedPrizeId === null) {
            alert('Selecciona un premio antes de asignarlo.');
            return;
        }

        const availablePrizes = classification.availablePrizes || [];
        const selectedPrize = availablePrizes.find(
            (p) => p.prizeId === selectedPrizeId
        );

        if (!selectedPrize) {
            alert(
                'El premio seleccionado ya no está disponible para este jugador.'
            );
            return;
        }

        // Comprobamos stock restante global justo antes de asignar
        const remainingStock = this.getRemainingStock(selectedPrizeId);
        if (remainingStock !== null && remainingStock <= 0) {
            alert(
                'Este premio ya no tiene stock disponible. Actualiza la página para ver los cambios.'
            );
            await this.loadData();
            return;
        }

        // Buscamos el miniGameId del premio en la lista de premios del torneo
        const prizeDto = prizes.find((p) => p.id === selectedPrizeId);
        const miniGameId = prizeDto ? prizeDto.miniGameId : null;

        const currentUserId = this.getCurrentUserId();

        const body = {
            tournamentId,
            miniGameId,
            playerId: classification.playerId,
            prizeId: selectedPrizeId,
            quantity: 1,
            pointsSpent: selectedPrize.pointsCost,
            createdByUserId: currentUserId
        };

        this.setState({ assigningForPlayerId: classification.playerId });

        try {
            const response = await Api.post('/api/PlayerPrizes', body);

            if (!response.ok) {
                throw new Error('No se pudo asignar el premio al jugador.');
            }

            // Recargamos clasificación y premios para reflejar cambios
            await this.loadData();
        } catch (error: any) {
            alert(
                error.message ||
                'Error al asignar el premio. Inténtalo de nuevo.'
            );
            this.setState({ assigningForPlayerId: null });
        }
    };

    private async handleDeleteAssignedPrize(
        assignedPrize: IAssignedPrizeDto
    ): Promise<void> {
        const confirmar = window.confirm(
            `¿Seguro que deseas eliminar el premio "${assignedPrize.name || 'Premio'}" asignado a este jugador?`
        );

        if (!confirmar) return;

        this.setState({ deletingPlayerPrizeId: assignedPrize.playerPrizeId });

        try {
            const response = await Api.delete(
                `/api/PlayerPrizes/${assignedPrize.playerPrizeId}`
            );

            if (!response.ok) {
                throw new Error('No se pudo eliminar el premio asignado.');
            }

            // Recargamos todo para recalcular puntos, stock, etc.
            await this.loadData();
        } catch (error: any) {
            alert(
                error.message || 'Error al eliminar el premio asignado.'
            );
            this.setState({ deletingPlayerPrizeId: null });
        }
    }

    // -----------------------------
    // Filtro de jugadores
    // -----------------------------

    private matchesSearch(
        classification: IPlayerClassificationDto,
        term: string
    ): boolean {
        const q = term.trim().toLowerCase();
        if (!q) return true;

        const parts: string[] = [];

        if (classification.fullName) parts.push(classification.fullName);
        if (classification.firstName) parts.push(classification.firstName);
        if (classification.lastName) parts.push(classification.lastName);

        return parts.some((p) => p.toLowerCase().includes(q));
    }

    // -----------------------------
    // Toolbar
    // -----------------------------

    private renderToolbar(): React.ReactNode {
        const { searchText } = this.state;

        return (
            <>
                {/* En esta pantalla no hay "Nuevo", sólo buscador */}
                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Buscar jugador..."
                    value={searchText}
                    onChange={this.handleSearchChange}
                    sx={{
                        ml: 'auto',
                        minWidth: { xs: '100%', sm: 220, md: 260 },
                        mt: { xs: 1, sm: 0 }
                    }}
                />
            </>
        );
    }

    // -----------------------------
    // Render secciones dentro de la tarjeta
    // -----------------------------

    private renderMiniGamePoints(
        miniGamePoints: IMiniGamePointsDto[] | null
    ): React.ReactNode {
        if (!miniGamePoints || miniGamePoints.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    Sin puntos por minijuego todavía.
                </Typography>
            );
        }

        return (
            <Box
                sx={{
                    mt: 1,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1
                }}
            >
                {miniGamePoints.map((mp) => (
                    <Box
                        key={mp.miniGameId}
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            bgcolor: 'grey.100',
                            borderRadius: 999
                        }}
                    >
                        <Typography variant="caption">
                            {mp.miniGameName || `Minijuego ${mp.miniGameId}`}:{' '}
                            <strong>{mp.points}</strong> pts
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    }

    private renderAssignedPrizes(
        assignedPrizes: IAssignedPrizeDto[] | null
    ): React.ReactNode {
        const { deletingPlayerPrizeId } = this.state;

        return (
            <Box sx={{ mt: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                    Premios asignados
                </Typography>

                {(!assignedPrizes || assignedPrizes.length === 0) && (
                    <Typography variant="body2" color="text.secondary">
                        Sin premios asignados todavía.
                    </Typography>
                )}

                {assignedPrizes &&
                    assignedPrizes.map((p) => (
                        <Box
                            key={p.playerPrizeId}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: 1,
                                mt: 0.5,
                                py: 1,
                                borderBottom: 'solid 1px #f4f4f4'
                            }}
                        >
                            <CheckCircleOutline sx={{color: 'success.main'}}/>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {p.name || `Premio ${p.prizeId}`} ({p.pointsCost} pts) — x
                                {p.quantity} — {this.formatDateTime(p.createdAt)}
                            </Typography>

                            <div style={{marginLeft: 'auto'}}>
                                <Tooltip title="Eliminar premio asignado" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => this.handleDeleteAssignedPrize(p)}
                                        disabled={
                                            deletingPlayerPrizeId === p.playerPrizeId
                                        }
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </Box>
                    ))}
            </Box>
        );
    }

    private renderAvailablePrizesSelect(
        classification: IPlayerClassificationDto
    ): React.ReactNode {
        const { selectedPrizeByPlayer, assigningForPlayerId } = this.state;

        const availablePoints = this.getAvailablePoints(classification);
        const allAvailable = classification.availablePrizes || [];

        // 🔥 Sólo premios que puede “pagar” con sus puntos disponibles
        // y que además tienen stock global > 0 (si stock está controlado)
        const affordablePrizes = allAvailable.filter((p) => {
            if (p.pointsCost > availablePoints) return false;

            const remainingStock = this.getRemainingStock(p.prizeId);
            if (remainingStock === null) {
                // sin límite de stock
                return true;
            }
            return remainingStock > 0;
        });

        const selectedPrizeId =
            selectedPrizeByPlayer[classification.playerId] ?? null;

        const assigning =
            assigningForPlayerId === classification.playerId;

        const hasAffordable = affordablePrizes.length > 0;
        const canAssign =
            hasAffordable && selectedPrizeId !== null && !assigning;

        return (
            <Stack
                spacing={1}
                direction={{ xs: 'column', sm: 'column' }}
                alignItems={{ xs: 'stretch', sm: 'stretch' }}
                sx={{ mt: { xs: 2, sm: 0 } }}
            >
                {/* Puntos disponibles */}
                <Typography variant="body2">
                    Puntos disponibles:{' '}
                    <strong>{availablePoints}</strong>
                </Typography>

                <Stack
                    spacing={1}
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    sx={{ mt: { xs: 2, sm: 0 } }}
                >
                    <FormControl
                        size="small"
                        fullWidth
                        sx={{ flex: 1 }}
                    >
                        <InputLabel
                            id={`prize-label-${classification.playerId}`}
                        >
                            Premios disponibles
                        </InputLabel>
                        <Select
                            labelId={`prize-label-${classification.playerId}`}
                            label="Premios disponibles"
                            value={
                                selectedPrizeId !== null
                                    ? String(selectedPrizeId)
                                    : ''
                            }
                            onChange={this.handlePrizeSelectChange(
                                classification.playerId
                            )}
                            disabled={!hasAffordable || assigning}
                        >
                            {!hasAffordable && (
                                <MenuItem value="">
                                    <em>Sin premios disponibles</em>
                                </MenuItem>
                            )}

                            {affordablePrizes.map((prize) => {
                                const remainingStock = this.getRemainingStock(
                                    prize.prizeId
                                );
                                const stockText =
                                    remainingStock === null
                                        ? ''
                                        : ` — stock: ${remainingStock}`;

                                return (
                                    <MenuItem
                                        key={prize.prizeId}
                                        value={prize.prizeId.toString()}
                                    >
                                        {prize.name || `Premio ${prize.prizeId}`} (
                                        {prize.pointsCost} pts{stockText})
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>

                    <Button
                        variant="contained"
                        startIcon={<EmojiEventsIcon />}
                        onClick={() => this.handleAssignPrize(classification)}
                        disabled={!canAssign}
                    >
                        {assigning ? 'Asignando...' : 'Asignar'}
                    </Button>
                </Stack>
            </Stack>
        );
    }

    // -----------------------------
    // Render tarjeta de jugador
    // -----------------------------

    private renderPlayerCard(
        classification: IPlayerClassificationDto,
        index: number
    ): React.ReactNode {
        const fullName =
            classification.fullName ||
            `${classification.firstName || ''} ${classification.lastName || ''
                }`.trim() ||
            `Jugador ${classification.playerId}`;

        return (
            <Grid key={classification.playerId} size={{ xs: 12 }}>
                <Card
                    sx={{
                        boxShadow: 4,
                        p: 2,
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'flex-start' },
                        gap: 2
                    }}
                >
                    {/* Ranking (posición) */}
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            bgcolor:
                                index === 0
                                    ? '#D6AF36'
                                    : index === 1
                                        ? '#A7A7AD'
                                        : index === 2
                                            ? '#A77044'
                                            : 'primary.main',
                            color: 'common.white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            flexShrink: 0
                        }}
                    >
                        {index + 1}
                    </Box>

                    {/* Info jugador */}
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" fontWeight={500}>
                            {fullName}
                        </Typography>

                        <Typography variant="subtitle2" color="text.secondary">
                            Puntos totales:{' '}
                            <strong>{classification.totalPoints}</strong>
                        </Typography>

                        {this.renderMiniGamePoints(
                            classification.miniGamePoints
                        )}

                        {this.renderAssignedPrizes(
                            classification.assignedPrizes
                        )}
                    </Box>

                    {/* Premios / Asignación */}
                    <Box
                        sx={{
                            flexShrink: 0,
                            width: { xs: '100%', md: 380 }
                        }}
                    >
                        {this.renderAvailablePrizesSelect(classification)}
                    </Box>
                </Card>
            </Grid>
        );
    }

    private renderGrid(): React.ReactNode {
        const { classifications, loading, error, searchText } = this.state;

        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return (
                <Typography color="error" variant="body1">
                    {error}
                </Typography>
            );
        }

        if (!classifications || classifications.length === 0) {
            return (
                <Typography variant="body1" color="text.secondary">
                    No hay jugadores en la clasificación todavía.
                </Typography>
            );
        }

        const filtered = classifications.filter((c) =>
            this.matchesSearch(c, searchText)
        );

        if (filtered.length === 0) {
            return (
                <Typography variant="body1" color="text.secondary">
                    No hay jugadores que coincidan con la búsqueda.
                </Typography>
            );
        }

        return (
            <Grid container spacing={2}>
                {filtered.map((c, index) =>
                    this.renderPlayerCard(c, index)
                )}
            </Grid>
        );
    }

    // -----------------------------
    // Render principal
    // -----------------------------

    public render(): React.ReactElement<ILeaderboardPageProps> {
        return (
            <AdminLayout
                entityTitle="Clasificación"
                entityIcon={<LeaderboardOutlined sx={{fontSize: 56}}/>}
                entityToolbar={this.renderToolbar()}
            >
                {this.renderGrid()}
            </AdminLayout>
        );
    }
}
